import React from "react";
import styled from "styled-components";
import {
    ShoppingCart,
    LocalShipping,
    Payment,
    CheckCircle,
    Receipt,
} from "@material-ui/icons";
import { mobile } from "../responsive";

// Checkout flow steps
const CHECKOUT_STEPS = [
    { key: "cart", label: "Cart", icon: ShoppingCart },
    { key: "shipping", label: "Delivery", icon: LocalShipping },
    { key: "payment", label: "Payment", icon: Payment },
    { key: "confirm", label: "Confirm", icon: Receipt },
];

// Styled Components
const StepperContainer = styled.div`
  background: linear-gradient(135deg, #017109 0%, #015a07 100%);
  border-radius: 12px;
  padding: ${(props) => (props.$compact ? "14px 18px" : "20px")};
  margin-bottom: ${(props) => (props.$compact ? "14px" : "20px")};
  box-shadow: 0 4px 20px rgba(1, 113, 9, 0.25);

  ${mobile({
    padding: "16px 12px",
    marginBottom: "16px",
})}
`;

const StepsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  ${mobile({
    justifyContent: "space-around",
})}
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  z-index: 1;
`;

const StepCircle = styled.div`
  width: ${(props) => (props.$compact ? "40px" : "48px")};
  height: ${(props) => (props.$compact ? "40px" : "48px")};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
        props.completed
            ? "#ffffff"
            : props.active
                ? "#ffffff"
                : "rgba(255, 255, 255, 0.2)"};
  color: ${(props) =>
        props.completed
            ? "#017109"
            : props.active
                ? "#017109"
                : "rgba(255, 255, 255, 0.5)"};
  transition: all 0.3s ease;
  box-shadow: ${(props) =>
        props.active
            ? "0 0 0 4px rgba(255, 255, 255, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)"
            : props.completed
                ? "0 2px 8px rgba(0, 0, 0, 0.15)"
                : "none"};
  border: ${(props) =>
        props.active ? "3px solid #4caf50" : "none"};

  svg {
    font-size: ${(props) => (props.$compact ? "21px" : "24px")};
  }

  ${mobile({
            width: "40px",
            height: "40px",
        })}
`;

const StepContent = styled.div`
  text-align: center;
  margin-top: ${(props) => (props.$compact ? "6px" : "8px")};
`;

const StepLabel = styled.div`
  font-size: 12px;
  font-weight: ${(props) => (props.active ? 700 : 500)};
  color: ${(props) =>
        props.completed || props.active
            ? "#ffffff"
            : "rgba(255, 255, 255, 0.6)"};
  white-space: nowrap;

  ${mobile({
                fontSize: "11px",
            })}
`;

const ConnectorLine = styled.div`
  position: absolute;
  top: ${(props) => (props.$compact ? "20px" : "24px")};
  left: calc(50% + 24px);
  right: calc(-50% + 24px);
  height: 3px;
  background: ${(props) =>
        props.completed
            ? "#ffffff"
            : "rgba(255, 255, 255, 0.2)"};
  z-index: 0;
  transition: background 0.3s ease;

  ${mobile({
                left: "calc(50% + 20px)",
                right: "calc(-50% + 20px)",
                top: "20px",
            })}
`;

/**
 * CheckoutStepper - Shows checkout flow progress
 * @param {string} currentStep - One of: 'cart', 'shipping', 'payment', 'confirm'
 */
const CheckoutStepper = ({ currentStep = "cart", compact = false }) => {
    const currentStepIndex = CHECKOUT_STEPS.findIndex(
        (step) => step.key === currentStep
    );

    return (
        <StepperContainer $compact={compact}>
            <StepsWrapper>
                {CHECKOUT_STEPS.map((step, index) => {
                    const isCompleted = index < currentStepIndex;
                    const isActive = index === currentStepIndex;
                    const IconComponent = step.icon;

                    return (
                        <React.Fragment key={step.key}>
                            <Step>
                                <StepCircle
                                    completed={isCompleted}
                                    active={isActive}
                                    $compact={compact}
                                >
                                    {isCompleted ? <CheckCircle /> : <IconComponent />}
                                </StepCircle>
                                <StepContent $compact={compact}>
                                    <StepLabel completed={isCompleted} active={isActive}>
                                        {step.label}
                                    </StepLabel>
                                </StepContent>
                                {index < CHECKOUT_STEPS.length - 1 && (
                                    <ConnectorLine
                                        completed={isCompleted}
                                        $compact={compact}
                                    />
                                )}
                            </Step>
                        </React.Fragment>
                    );
                })}
            </StepsWrapper>
        </StepperContainer>
    );
};

export default CheckoutStepper;
